import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { COLLECTIONS, roundGoldPrice, type GoldPriceCurrent } from "@gull/shared";
import { db } from "../lib/firebase";

const KARATS = [24, 22, 18, 14] as const;
type DisplayKarat = (typeof KARATS)[number];

function formatGoldPrice(price: number | null) {
  if (price == null || !Number.isFinite(price) || price <= 0) return "—";

  const rounded = roundGoldPrice(price);
  const hasDecimals = Math.abs(rounded - Math.round(rounded)) > 0.005;

  const formatted = rounded.toLocaleString("nb-NO", {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: hasDecimals ? 2 : 0,
  });

  return hasDecimals ? formatted : `${formatted},-`;
}

function formatUpdatedAt(ms: number | null) {
  if (!ms) return null;
  return new Date(ms).toLocaleString("nb-NO", {
    timeZone: "Europe/Oslo",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function PriceCard() {
  const [selectedKarat, setSelectedKarat] = useState<DisplayKarat>(24);
  const [currentPrices, setCurrentPrices] = useState<Record<number, number>>({});
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);

  useEffect(() => {
    return onSnapshot(doc(db, COLLECTIONS.goldPrices, "current"), (snap) => {
      const current = snap.exists() ? (snap.data() as GoldPriceCurrent) : null;
      setCurrentPrices(current?.prices ?? {});
      setUpdatedAt(current?.scrapedAt ?? current?.updatedAt ?? null);
    });
  }, []);

  const selectedPrice = currentPrices[selectedKarat] ?? null;
  const formattedUpdatedAt = formatUpdatedAt(updatedAt);

  return (
    <section className="landing-price-card" aria-label="Dagens gullpris">
      <div className="landing-price-card__body">
        <div>
          <p className="landing-price-card__label">Dagens gullpris (NOK per gram)</p>
          <div className="landing-karat-list" aria-label="Velg karat">
            {KARATS.map((karat) => (
              <button
                key={karat}
                type="button"
                className="landing-karat-option"
                aria-pressed={selectedKarat === karat}
                onClick={() => setSelectedKarat(karat)}
              >
                <span className="landing-karat-dot" aria-hidden="true" />
                <span>{karat}k</span>
              </button>
            ))}
          </div>
        </div>

        <div className="landing-price-card__value">
          <a className="landing-karat-link" href="#hva-er-karat-tekst">
            Hva er karat?
          </a>
          <div className="landing-gold-price">{formatGoldPrice(selectedPrice)}</div>
          {formattedUpdatedAt ? (
            <div className="landing-price-meta">Oppdatert {formattedUpdatedAt}</div>
          ) : null}
        </div>
      </div>

      <Link className="landing-cta" to="/bestill-pose">
        <span>Kom i gang</span>
        <span aria-hidden="true">→</span>
      </Link>
    </section>
  );
}

export default function Landing() {
  return (
    <main className="landing-page">
      <div className="landing-shell">
        <section className="landing-hero">
          <h1>Selg gull til oss</h1>
          <p>
            Vi tilbyr en trygg og profesjonell tjeneste for innlevering og salg av gull. Med vår
            lange tradisjon for kvalitet og ekspertise, sikrer vi en smidig prosess og
            konkurransedyktige priser basert på gjeldende markedsverdi.
          </p>
        </section>

        <PriceCard />

        <section className="landing-section">
          <h2>En begrenset ressurs</h2>
          <p>
            Gull er en begrenset ressurs, og resirkulering gjør det mulig å bruke det om igjen uten
            å miste verdi. Dette reduserer behovet for ny utvinning, som har stor miljøpåvirkning,
            og bidrar dermed til en mer bærekraftig fremtid.
          </p>
        </section>

        <section className="landing-section">
          <h2>Hva er karat?</h2>
          <p id="hva-er-karat-tekst">
            Karat beskriver hvor stor andel rent gull metallet inneholder. 24k er rent gull, 18k er
            75% gull og 14k er 58,5% gull.
          </p>
        </section>

        <section className="landing-section" id="om-da">
          <h2>Hvorfor velge oss?</h2>
          <div className="landing-reasons">
            <article className="landing-reason">
              <h3>Trygghet og profesjonalitet</h3>
              <p>
                Som en av Norges mest anerkjente gullsmeder, garanterer vi en sikker og transparent
                prosess. Alle ledd i prosessen følger strenge retningslinjer for ansvarlig
                metallhandel.
              </p>
            </article>

            <article className="landing-reason">
              <h3>Konkurransedyktige priser</h3>
              <p>
                Prisen beregnes ut fra den aktuelle gullprisen i markedet, fra dag til dag, slik at
                du får en rettferdig og gunstig verdi for gjenstandene dine.
              </p>
            </article>

            <article className="landing-reason">
              <h3>Ekspertvurdering og kvalitetssikring</h3>
              <p>
                Våre spesialister veier og analyserer metallet for å fastslå renhet og verdi med
                stor presisjon.
              </p>
            </article>
          </div>
        </section>

        <section className="landing-process" id="tjenester">
          <h2>Slik foregår prosessen</h2>
          <div className="landing-steps">
            <article>
              <h3>1. Bestill time eller kom innom en av våre butikker</h3>
              <p>
                Innlevering kan gjøres etter avtale via vårt bookingsystem dersom din nærmeste
                butikk tilbyr dette. Ved innlevering av større mengder ber vi deg kontakte butikken
                på forhånd dersom booking ikke er tilgjengelig.
              </p>
            </article>

            <article>
              <h3>2. Vurdering av metallene</h3>
              <p>Vi veier, analyserer og fastslår renheten til gjenstanden.</p>
            </article>

            <article>
              <h3>3. Pristilbud</h3>
              <p>Du mottar et tilbud basert på dagens markedspris.</p>
            </article>

            <article>
              <h3>4. Oppgjør</h3>
              <p>Velg mellom:</p>
              <ul>
                <li>· Utbetaling til bankkonto</li>
                <li>· Gavekort hos David-Andersen</li>
              </ul>
            </article>
          </div>

          <Link className="landing-cta" to="/bestill-pose">
            <span>Kom i gang</span>
            <span aria-hidden="true">→</span>
          </Link>
        </section>
      </div>
    </main>
  );
}
